import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";

const Tipmodal: React.FC<{
  total: number;
  onTipChange?: (tipAmount: number) => void;
  ordertype: "delivery" | "pickup";
  defaultTip?: boolean;
  deliveryTipToPartner?: boolean;
  baseTipPercent?: number;
  tipGapPercent?: number;
}> = ({
  total,
  onTipChange,
  ordertype,
  defaultTip = false,
  deliveryTipToPartner = true,
  baseTipPercent,
  tipGapPercent,
}) => {
  const { currencySymbol } = useAuth();
  const [selectedTip, setSelectedTip] = useState<number | null>(null);
  const [customTip, setCustomTip] = useState<string>("");
  const [isEditingCustom, setIsEditingCustom] = useState(false);
  const [currentTipAmount, setCurrentTipAmount] = useState<number>(0);

  const tipPercentages = useMemo(() => {
    const hasPreset =
      typeof baseTipPercent === "number" &&
      baseTipPercent > 0 &&
      typeof tipGapPercent === "number" &&
      tipGapPercent > 0;

    if (!hasPreset) return [10, 15, 20];

    return [
      baseTipPercent,
      baseTipPercent + tipGapPercent,
      baseTipPercent + tipGapPercent * 2,
    ];
  }, [baseTipPercent, tipGapPercent]);

  // Clear custom tip when total becomes null or 0
  useEffect(() => {
    if (!total) {
      setCustomTip("");
      setSelectedTip(null);
      if (currentTipAmount !== 0) {
        setCurrentTipAmount(0);
        onTipChange?.(0);
      }
    }
  }, [total]);

const Tipnow = (n: number) => Number(n.toFixed(2));

const calculateTipAmount = (percentage: number): number => {
  return Tipnow((total * percentage) / 100);
};

// NEW: keep stored tip in sync when total changes
useEffect(() => {
  if (total <= 0) return;

  if (selectedTip !== null && !customTip) {
    const newTipAmount = calculateTipAmount(selectedTip);
    if (currentTipAmount !== newTipAmount) {
      setCurrentTipAmount(newTipAmount);
      onTipChange?.(newTipAmount);
    }
  }
}, [total, selectedTip, customTip, currentTipAmount, onTipChange]);

useEffect(() => {
  // Only preselect tip if defaultTip is enabled for the restaurant
  if (defaultTip && total > 0 && selectedTip === null && !customTip) {
    setSelectedTip(tipPercentages[0]);
    const defaultTipAmount = calculateTipAmount(tipPercentages[0]);
    setCurrentTipAmount(defaultTipAmount);
    onTipChange?.(defaultTipAmount);
  }
  else if (!defaultTip) {
    setSelectedTip(null);
    setCurrentTipAmount(0);
    onTipChange?.(0);
  }
}, [total, defaultTip, tipPercentages]);
  

  const calculateCustomPercentage = (): number => {
    const tipAmount = Number(customTip);
    if (!tipAmount || total === 0) return 0;
    return (tipAmount / total) * 100;
  };

  // Handle tip selection with toggle functionality
const handlePercentageTip = (percentage: number) => {
  if (selectedTip === percentage) {
    setSelectedTip(null);
    if (currentTipAmount !== 0) {
      setCurrentTipAmount(0);
      onTipChange?.(0);
    }
  } else {
    setSelectedTip(percentage);
    setCustomTip("");
    setIsEditingCustom(false);
    const newTipAmount = calculateTipAmount(percentage);
    if (currentTipAmount !== newTipAmount) {
      setCurrentTipAmount(newTipAmount);
      onTipChange?.(newTipAmount);
    }
  }
};


  // Handle custom tip blur
  const handleCustomTipBlur = () => {
    setIsEditingCustom(false);
    const newTipAmount = customTip ? Number(customTip) : 0;

    // Only call onTipChange if the value actually changed
    if (currentTipAmount !== newTipAmount) {
      setCurrentTipAmount(newTipAmount);
      onTipChange?.(newTipAmount);
    }

    // If custom tip is empty or 0, clear it
    if (!newTipAmount) {
      setCustomTip("");
    }
  };

  // Handle custom tip input click
  const handleCustomClick = () => {
    setIsEditingCustom(true);
    setSelectedTip(null);
  };

  return (
    <div className="bg-white rounded-lg border-[1px] border-black/10 p-4 mb-4 dm-tip-modal">
      <p className="font-semibold text-[17px] sm:text-[19px] text-black mb-4">
        {ordertype === "pickup"
          ? "Tip your team members?"
          : deliveryTipToPartner
            ? "Add a tip for your driver?"
            : "Tip your team members?"}
      </p>

      <div className="grid grid-cols-4 sm:gap-3 gap-2 sm:w-[70%]">
        {/* Percentage buttons */}
        {tipPercentages.map((percentage) => (
          <button
            key={percentage}
            onClick={() => handlePercentageTip(percentage)}
            className={`dm-tip-option flex-1 min-w-[70px] sm:min-w-[80px] h-[72px] sm:h-[76px] bg-white rounded-xl border-2 transition-all hover:border-gray-400
              ${
                selectedTip === percentage
                  ? "border-black dm-tip-option-selected"
                  : "border border-gray-50 dm-tip-option-unselected"
              }
            `}
          >
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-[16px] sm:text-lg font-bold text-black">
                {percentage}%
              </div>
              <div className="text-[13px] sm:text-sm text-gray-600">
                {currencySymbol}{calculateTipAmount(percentage).toFixed(2)}
              </div>
            </div>
          </button>
        ))}

        {/* Custom tip box */}
        <button
          onClick={handleCustomClick}
          className={`dm-tip-option flex-1 min-w-[70px] sm:min-w-[80px] h-[72px] sm:h-[76px] bg-white rounded-xl border-2 transition-all
            ${
              customTip
                ? "border-black dm-tip-option-selected"
                : "border border-gray-50 dm-tip-option-unselected"
            } hover:border-gray-400`}
        >
          {isEditingCustom ? (
         <input
  autoFocus
  type="number"
  step="0.01"
  min="0"
  value={customTip}
  onChange={(e) => {
    let val = e.target.value;
    if (val === "") {
      setCustomTip(val);
      return;
    }
    const regex = /^\d{0,3}(\.\d{0,2})?$/;

    if (regex.test(val)) {
      setCustomTip(val);
    }
  }}
  onBlur={handleCustomTipBlur}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      handleCustomTipBlur();
    }
  }}
  className="w-full h-full text-center text-[16px] sm:text-lg font-bold bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
  placeholder="0.00"
/>
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-1">
              {customTip ? (
                <>
                  <div className="text-[16px] sm:text-lg font-bold text-black">
                    {calculateCustomPercentage().toFixed(0)}%
                  </div>
                  <div className="text-[13px] sm:text-sm text-gray-600">
                    {currencySymbol}{Number(customTip).toFixed(2)}
                  </div>
                </>
              ) : (
                <span className="text-[13px] sm:text-sm text-black">
                  Custom
                </span>
              )}
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default Tipmodal;
